import { HttpErrorResponse } from '@angular/common/http';

/**
 * Copy used when the API sends no usable message for a given status.
 *
 * The auth interceptor already handles 401/403 retries and its own "Access denied" toast, but
 * these remain as a fallback for the cases it lets through.
 */
const STATUS_FALLBACKS: Readonly<Record<number, string>> = {
  0: 'Could not reach the server. Check your connection and try again.',
  400: 'Some of the details you entered are not valid. Please check them and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: "You don't have permission to do this.",
  404: 'We could not find what this request needs. Please refresh and try again.',
  409: 'That conflicts with something that already exists.',
  429: 'Too many attempts. Please wait a moment and try again.',
  502: 'A service we rely on is unavailable right now. Please try again shortly.',
  503: 'A service we rely on is unavailable right now. Please try again shortly.',
  504: 'That request took too long. Please try again.',
};

/** Angular's message for an aborted/offline request — never worth showing a user. */
const UNKNOWN_HTTP_ERROR = 'Http failure response for (unknown url): 0 Unknown Error';

/**
 * Generic framework strings that carry no information. Nest sends
 * `{ message: 'Internal server error' }` on a 500, which is worse than our own copy.
 */
const USELESS_BODY_MESSAGES = new Set([
  'internal server error',
  'error',
  'bad request',
  'bad request exception',
  'forbidden',
  'forbidden resource',
  'not found',
  'conflict',
  'something went wrong.',
]);

function messageFromBody(body: unknown): string | undefined {
  if (typeof body === 'string') {
    const trimmed = body.trim();
    // Skip HTML error pages from a proxy or load balancer.
    return trimmed && !trimmed.startsWith('<') ? trimmed : undefined;
  }
  if (!body || typeof body !== 'object') {
    return undefined;
  }
  const record = body as { message?: unknown; error?: { message?: unknown } };
  const raw = record.message ?? record.error?.message;

  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }
  if (Array.isArray(raw)) {
    // Nest's ValidationPipe sends a string[] of field messages.
    const parts = raw.map((item) => String(item)).filter((item) => item.trim());
    if (parts.length) {
      return parts.join(', ');
    }
  }
  return undefined;
}

/**
 * Best human-readable message for any thrown/emitted API error.
 *
 * Prefers the server's own `message` — our API sends actionable copy such as "This person is
 * already a team member of this brand." — and falls back to per-status text when the body is
 * missing or carries only generic framework wording.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    const message = messageFromBody(error.error);
    if (message && !USELESS_BODY_MESSAGES.has(message.toLowerCase())) {
      return message;
    }
    return (
      STATUS_FALLBACKS[error.status] ?? (error.status >= 500 ? STATUS_FALLBACKS[502] : fallback)
    );
  }
  if (error instanceof Error && error.message && error.message !== UNKNOWN_HTTP_ERROR) {
    return error.message;
  }
  return fallback;
}

/**
 * Field-level messages when the API sent a validation array, so a caller can render them
 * against individual inputs. Empty for every other kind of error.
 */
export function apiErrorFieldMessages(error: unknown): string[] {
  if (!(error instanceof HttpErrorResponse) || !error.error || typeof error.error !== 'object') {
    return [];
  }
  const record = error.error as { errors?: unknown; message?: unknown };
  const raw = Array.isArray(record.errors) ? record.errors : record.message;
  return Array.isArray(raw) ? raw.map((item) => String(item)).filter((item) => item.trim()) : [];
}

/** HTTP status for an `HttpErrorResponse`, `undefined` for anything else. */
export function apiErrorStatus(error: unknown): number | undefined {
  return error instanceof HttpErrorResponse ? error.status : undefined;
}

/** Stable machine-readable code the API attaches to structured errors (e.g. ALREADY_MEMBER). */
export function apiErrorCode(error: unknown): string | undefined {
  if (!(error instanceof HttpErrorResponse) || !error.error || typeof error.error !== 'object') {
    return undefined;
  }
  const code = (error.error as { code?: unknown }).code;
  return typeof code === 'string' && code.trim() ? code : undefined;
}
